import React, { useState, useEffect } from 'react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, LineChart, Line, ReferenceLine } from 'recharts';
import { useApp } from '../context/AppContext';
import { getDashboardStats, getRevenueAnalytics } from '../services/api';

const Tip = ({ active, payload, label }) => active && payload?.length ? (
  <div style={{ background:'#131B1B', border:'1px solid #2a3a3a', borderRadius:8, padding:'8px 12px', fontSize:12 }}>
    <p style={{ color:'#8a9a97', marginBottom:4 }}>{label}</p>
    {payload.map((p,i) => <p key={i} style={{ color:p.color }}>{p.name}: ${Number(p.value).toLocaleString()}</p>)}
  </div>
) : null;

const QUARTERS = ['Q1 2026','Q2 2026','Q3 2026','Full year'];

export default function SalesForecast() {
  const { token } = useApp();
  const [stats,   setStats]   = useState(null);
  const [revenue, setRevenue] = useState([]);
  const [quarter, setQuarter] = useState('Q2 2026');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function load() {
      setLoading(true);
      try {
        const [s, r] = await Promise.allSettled([
          getDashboardStats(token),
          getRevenueAnalytics(token, 'month'),
        ]);
        if (s.status === 'fulfilled') setStats(s.value.data || s.value);
        if (r.status === 'fulfilled') {
          const rd = r.value.data || r.value;
          setRevenue(Array.isArray(rd) ? rd : []);
        }
      } finally { setLoading(false); }
    }
    load();
  }, [token]);

  const grossRevenue = stats?.grossRevenue || 0;
  const netRevenue   = stats?.netRevenue   || 0;
  const dealsTotal   = stats?.dealsTotal   || 0;
  const annualTarget = 60000;
  const attainmentPct = Math.round((grossRevenue / annualTarget) * 100);

  // Build chart data from real revenue
  const chartData = revenue.map(m => ({
    month: m.month,
    closed: m.gross || 0,
    net:    m.net   || 0,
    target: annualTarget / 12,
  }));

  if (loading) return (
    <div style={{ display:'flex', alignItems:'center', justifyContent:'center', height:300, color:'var(--text-dim)', fontSize:14 }}>
      Loading forecast…
    </div>
  );

  return (
    <div>
      {/* KPI Cards */}
      <div className="stats-grid" style={{ marginBottom: 24 }}>
        <div className="stat-card">
          <div className="stat-label">Closed won</div>
          <div className="stat-value" style={{ color:'var(--accent)' }}>${grossRevenue.toLocaleString()}</div>
          <div className="stat-sub">{attainmentPct}% of annual target</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Net revenue</div>
          <div className="stat-value" style={{ color:'#4fc3f7' }}>${netRevenue.toLocaleString()}</div>
          <div className="stat-sub">After fees</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Annual target</div>
          <div className="stat-value">${annualTarget.toLocaleString()}</div>
          <div className="stat-sub">{100 - attainmentPct}% remaining</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Active deals</div>
          <div className="stat-value">{dealsTotal}</div>
          <div className="stat-sub">In pipeline</div>
        </div>
      </div>

      {/* Quarter filter */}
      <div style={{ display:'flex', gap:8, marginBottom:20 }}>
        {QUARTERS.map(q => (
          <button key={q} onClick={() => setQuarter(q)}
            style={{ padding:'6px 16px', borderRadius:20, border:'1px solid var(--border)', cursor:'pointer', fontSize:13,
              background: quarter===q ? 'var(--accent)' : 'var(--bg-input)',
              color: quarter===q ? '#0a0a0a' : 'var(--text-muted)', fontWeight: quarter===q ? 600 : 400 }}>
            {q}
          </button>
        ))}
      </div>

      {/* Charts */}
      <div className="two-col" style={{ marginBottom: 24 }}>
        <div className="card">
          <div className="card-header">
            <span className="card-title">Revenue by month</span>
          </div>
          {chartData.length > 0 ? (
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={chartData} margin={{ top:4, right:4, left:-20, bottom:0 }}>
                <XAxis dataKey="month" tick={{ fill:'#646B6B', fontSize:11 }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fill:'#646B6B', fontSize:11 }} axisLine={false} tickLine={false} tickFormatter={v => `$${v}`} />
                <Tooltip content={<Tip />} />
                <Bar dataKey="closed" name="Gross" fill="#A7F432" radius={[3,3,0,0]} />
                <Bar dataKey="net"    name="Net"   fill="#4fc3f7" radius={[3,3,0,0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div style={{ height:240, display:'flex', alignItems:'center', justifyContent:'center', color:'var(--text-dim)', fontSize:13 }}>
              No revenue data yet
            </div>
          )}
        </div>

        <div className="card">
          <div className="card-header">
            <span className="card-title">Target attainment</span>
          </div>
          {chartData.length > 0 ? (
            <ResponsiveContainer width="100%" height={240}>
              <LineChart data={chartData} margin={{ top:4, right:4, left:-20, bottom:0 }}>
                <XAxis dataKey="month" tick={{ fill:'#646B6B', fontSize:11 }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fill:'#646B6B', fontSize:11 }} axisLine={false} tickLine={false} tickFormatter={v => `$${v}`} />
                <Tooltip content={<Tip />} />
                <ReferenceLine y={annualTarget/12} stroke="#ff4d4d" strokeDasharray="4 4" label={{ value:'Monthly target', fill:'#ff4d4d', fontSize:10 }} />
                <Line type="monotone" dataKey="closed" name="Gross" stroke="#A7F432" strokeWidth={2} dot={{ fill:'#A7F432', r:4 }} />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div style={{ height:240, display:'flex', alignItems:'center', justifyContent:'center', color:'var(--text-dim)', fontSize:13 }}>
              No data yet
            </div>
          )}
        </div>
      </div>

      {/* Summary */}
      <div className="card">
        <div className="card-header"><span className="card-title">Revenue summary</span></div>
        <div style={{ padding:'16px 0' }}>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:16 }}>
            <div style={{ textAlign:'center', padding:16, background:'var(--bg-input)', borderRadius:8 }}>
              <div style={{ fontSize:11, color:'var(--text-dim)', marginBottom:6 }}>GROSS REVENUE</div>
              <div style={{ fontSize:24, fontWeight:700, color:'var(--accent)' }}>${grossRevenue.toLocaleString()}</div>
            </div>
            <div style={{ textAlign:'center', padding:16, background:'var(--bg-input)', borderRadius:8 }}>
              <div style={{ fontSize:11, color:'var(--text-dim)', marginBottom:6 }}>NET REVENUE</div>
              <div style={{ fontSize:24, fontWeight:700, color:'#4fc3f7' }}>${netRevenue.toLocaleString()}</div>
            </div>
            <div style={{ textAlign:'center', padding:16, background:'var(--bg-input)', borderRadius:8 }}>
              <div style={{ fontSize:11, color:'var(--text-dim)', marginBottom:6 }}>ATTAINMENT</div>
              <div style={{ fontSize:24, fontWeight:700, color: attainmentPct >= 100 ? '#A7F432' : attainmentPct >= 50 ? '#f5a623' : '#ff4d4d' }}>
                {attainmentPct}%
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
